import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReleasePackage } from "interface/package";
import { GrideDateFormatter } from "ui/Date";
import { ICON_NAMES } from "utils/constant";
import { FieldFull, FieldSplit, FieldRow } from "ui/CardField";
import { formatDate, formatAmount, formatWeight, getValue } from "utils/formatters";

type ReleasePackageItem = ReleasePackage & Record<string, any>;

interface ReleasedPackagesMobileListProps {
	items: ReleasePackageItem[];
	loading: boolean;
	onOpenPdf: (item: ReleasePackage) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (item: ReleasePackageItem) => {
	const signedByName = getValue<string>(item, "signedByName", "SignedByName");
	const salesConfirmationStatusCode = getValue<string>(
		item,
		"salesConfirmationStatusCode",
		"SalesConfirmationStatusCode",
	);

	if (!signedByName) {
		return "released-package-list-card--status-red";
	}

	if (!salesConfirmationStatusCode) {
		return "released-package-list-card--status-yellow";
	}

	return "released-package-list-card--status-green";
};

const formatVessel = (item: ReleasePackageItem) => {
	const registration = item.vesselRegistrationId?.trim();
	const vesselName = item.vesselName?.trim();

	if (registration && vesselName) {
		return `${registration} - ${vesselName}`;
	}

	return registration || vesselName || "-";
};

const ReleasedPackagesMobileList: FC<ReleasedPackagesMobileListProps> = ({
	items,
	loading,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	return (
		<div className="released-package-list-mobile" onScroll={handleScroll}>
			{empty && (
				<div className="released-package-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const reportGuid = getValue<string>(item, "reportGuid", "ReportGuid");

				const itemKey = `${item.registrationId ?? "noreg"}-${item.packageId ?? "nopackage"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`released-package-list-card ${getStatusClassName(item)}`}
					>
						<div className="released-package-list-card-header">
							<div className="released-package-list-card-note"><strong>{item.registrationId || "-"} - {item.packageId || "-"}</strong></div>
							<div className="released-package-list-card-date">
								{item.confirmedTime ? (
									<GrideDateFormatter
										date={item.confirmedTime}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="released-package-list-card-body">
							<FieldRow
								items={[
									{ label: t("Buyer"), value: item.buyerName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Vessel"), value: formatVessel(item) },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Terminal"), value: item.terminalName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: "Innm.dato", value: formatDate(item.registeredTime) },
									{ label: t("Type"), value: item.salesType || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Weight"), value: formatWeight(item.liveWeightKg) },
									{ label: t("Value"), value: formatAmount(item.amount) },
								]}
							/>
							<FieldRow
								items={[
									{ label: "Signert av", value: item.signedByName || "-" },
								]}
							/>
						</div>

						<div className="released-package-list-card-actions">
							<button
								type="button"
								className="released-package-list-icon-btn"
								onClick={() => onOpenPdf({ ...item, reportGuid } as ReleasePackage)}
								title={t("Download")}
								disabled={!item.reportGuid}
							>
								<img
									src={`/icon/${ICON_NAMES.Download}.svg`}
									alt={t("Download")}
								/>
							</button>
						</div>
					</div>
				);
			})}

			{loading && (
				<div className="released-package-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default ReleasedPackagesMobileList;